import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';

type Attributes = {
	[key: string]: boolean | string | number | undefined | null | Attributes;
};

/**
 * Logs an error using FAMA, which uses the GASv3 analytics pipeline and allows consumption from SignalFx,
 * Splunk and Sentry for monitoring and alerting. The action subject for the generated analytics event will
 * be `${packageName}.${errorId}` (e.g. 'jiraProjectSettingsAppsIssueTypePage.addIssueType') and the
 * action will be 'failed'.
 *
 * Taking the example event above, this is how it can be consumed from different platforms:
 *
 * SignalFx (full event name can be obtained in Data Portal):
 *   'analytics.jira.operational.jiraProjectSettingsAppsAddIssueType.addIssueType_failed.count'
 *
 * Splunk:
 *   'eventtype="micros_analytics-service" ext.event.eventName="jiraProjectSettingsAppsAddIssueType.addIssueType failed"'
 *
 * Sentry:
 *   'logger:jiraProjectSettingsAppsAddIssueType.addIssueType'
 *
 * DO NOT LOG USER GENERATED CONTENT OR PERSONAL DATA.
 *
 * @see https://hello.atlassian.net/wiki/spaces/MEASURE/blog/2019/11/19/590258982/FAMA+for+all
 * @see https://hello.atlassian.net/wiki/spaces/ALBS/pages/589347147/Jira+monitoring+frontend+errors
 *
 * @param packageName Camel case package name (e.g. 'jiraProjectSettingsAppsIssueTypePage')
 * @param errorId Camel case functionality identifier (e.g. 'addIssueType')
 * @param message Brief description of the error
 * @param error JavaScript error object to be sent to Sentry / Splunk (optional)
 * @param attributes Object of additional analytics attributes (optional)
 */
export const logError = (
	packageName: string,
	errorId: string,
	message: string,
	error?: Error,
	attributes?: Attributes,
): void => {
	// Send error log to analytics pipeline (i.e. SFX) and Sentry
	fireErrorAnalytics({
		meta: {
			packageName,
			id: errorId,
			teamName: 'morphinecake',
		},
		attributes: {
			...attributes,
			message,
		},
		error, // this error is only sent to Sentry
	});

	// Send error log to Splunk
	// We use this instead of `sendToPrivacyUnsafeSplunk` to preserve error message and custom properties
	const location = `${packageName}.${errorId}`;
	log.unsafeErrorWithCustomerData(location, message, error);
};
